import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Product, ProductReference, ProductService } from '@spartacus/core';
import { CurrentProductService } from '@spartacus/storefront';
import { combineLatest, map, Observable, of, Subject, take } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import { SdShowMoreComponent } from '@seb/web-design-system/show-more';
import { SdProductCardComponent } from '@seb/web-design-system/product-card';
import { SdProductSellerModalComponent } from '@seb/web-design-system/product-seller';
import {
  BrandsEnum,
  BrandThemesEnum,
  DeviceService, IComponentWrapper,
  IProductCard,
  SdComponentWrapperComponent,
  SizesEnum
} from '@seb/web-design-system/core';
import {
  ProductNotificationModalComponent
} from '@seb/web-common/core/components/static/product-notification-modal/product-notification-modal.component';
import { PdpConfig } from '@seb/web-common/core/models';
import { ProductScopeEnum } from '@seb/web-common/core/enums';
import { BrandCornerMapper, ProductMapper } from '@seb/web-common/core/mappers';
import {
  BaseStoreService,
  CartFacadeService,
  RangeService,
  StockService,
  TrackingService
} from '@seb/web-common/core/services';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'wc-product-range',
  templateUrl: './product-range.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdComponentWrapperComponent,
    SdShowMoreComponent,
    SdProductCardComponent,
    SdProductSellerModalComponent,
    ProductNotificationModalComponent
  ]
})
export class ProductRangeComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject();
  constructor(
    private readonly currentProductService: CurrentProductService,
    private readonly rangeService: RangeService,
    private readonly productMapper: ProductMapper,
    private readonly productService: ProductService,
    private readonly cartFacadeService: CartFacadeService,
    private translateService: TranslateService,
    private readonly deviceService: DeviceService,
    private readonly baseStoreService: BaseStoreService,
    private trackingService: TrackingService,
    private cdRef: ChangeDetectorRef,
    private readonly brandCornerMapper: BrandCornerMapper,
    private readonly stockService: StockService,
    @Inject(PLATFORM_ID) protected platformId?: any
  ) {}

  sizesEnum = SizesEnum;
  productCodes: string[] = [];
  productCards: IProductCard[] = [];
  products: Product[] = [];
  notifyModalOpened = false;
  notifyProduct = '';
  notifyProductName = '';
  wrapperContent: IComponentWrapper;

  isOpenedSellerModal = false;
  showRange = false;
  showedRow = 0;
  isMobile = false;
  isTablet = false;
  lameConfig: PdpConfig;
  isNewPriceManagementActivated: boolean;
  isSSR = true;

  @Input() brand = BrandsEnum.default;

  @Output()
  isRangeEmpty = new EventEmitter<boolean>();

  ngOnInit(): void {
    const baseStore = this.baseStoreService.baseStore;
    const isMobile$ = this.deviceService.isMobile;
    const isTablet$ = this.deviceService.isTablet;
    const currentProduct$ = this.currentProductService.getProduct().pipe(
      switchMap((product: Product) => {
        return this.rangeService.getRange(product.code);
      })
    );

    this.lameConfig = this.brandCornerMapper.toWcConfig(this.brand)?.pdp;

    combineLatest([isMobile$, isTablet$, currentProduct$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isMobile, isTablet, productsInRange]: [boolean, boolean, { references: ProductReference[] }]) => {
        this.isMobile = isMobile;
        this.isTablet = isTablet;
        this.productCodes = [];
        this.isNewPriceManagementActivated = baseStore.activateNewPriceManagement;

        this.isRangeEmpty.emit(productsInRange.references.length === 0);
        productsInRange.references.forEach((reference: ProductReference) => {
          this.productCodes.push(reference.target.code);
        });
        this.loadProducts(this.productCodes);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addToCart(productCode: string): void {
    const product = this.products.find((p) => p.code === productCode);
    this.cartFacadeService.addToCartShippingType(product, 1);
  }

  notifyMe(productCode: string): void {
    const currentProductCard = this.productCards.find((productCard) => productCard.code === productCode);
    this.notifyProductName = currentProductCard.title;
    this.notifyProduct = productCode;
    this.notifyModalOpened = true;
  }

  private loadProducts(productCodes: string[]): void {
    this.productCards = [];
    this.products = [];
    const products$: Observable<Product>[] = productCodes.map((code) => {
      return this.productService.get(code, ProductScopeEnum.CARD);
    });
    const cards: IProductCard[] = [];
    products$.forEach((product$: Observable<Product>) => {
      product$
        .pipe(
          filter((p) => !!p),
          switchMap((product) => {
            return combineLatest([of(product), this.productMapper.productToProductCard(product)]);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe(([product, productCard]) => {
          this.products.push(product);
          const card = productCard;
          const index = this.productCodes.findIndex((code) => code === card.code);
          cards[index] = card;
          this.productCards = cards;
          if (this.productCards.length === this.productCodes.length) {
            this.showRange = true;
          }

          if (this.isMobile) {
            this.showedRow = this.productCards.length;
          } else if (this.isTablet) {
            this.showedRow = Math.ceil(this.productCards.length / 2);
          } else {
            this.showedRow = Math.ceil(this.productCards.length / 3);
          }

          this.wrapperContent = {
            titleLevel: 2,
            links: [],
            footNotes: [],
            heading: {
              titleThemesConfig:
                this.brandCornerMapper.toWcConfig(this.brand)?.pdp?.range?.titleTheme || BrandThemesEnum.brand,
              isUnderlined: !!this.brandCornerMapper.toWcConfig(this.brand)?.pdp?.range?.titleUnderlined,
              size: SizesEnum.l,
              data: {
                title: `${this.translateService.instant('pdp.sectionTitles.range')} &nbsp;<strong>(${
                  this.productCards.length
                })</strong>`
              }
            }
          };

          this.updateProductCards();
          this.cdRef.markForCheck();
        });
    });
  }

  onProductClick(productCodeClicked: string): void {
    const currentProduct = this.products.find((product: Product) => product.code === productCodeClicked);
    this.trackingService.sendEvent(
      'productClick',
      this.trackingService.productToEventProduct(
        currentProduct,
        this.productCards.findIndex((card) => card.code === productCodeClicked)
      )
    );
  }

  openSellerModal(): void {
    this.isOpenedSellerModal = true;
  }

  updateProductCards() {
    if (isPlatformBrowser(this.platformId)) {
      const updateProductPrices$: Observable<IProductCard>[] = this.productCards
        .map((product: IProductCard) => {
          if (!product.isBundle) {
            return this.productService.get(product.code, [ProductScopeEnum.PRICE, ProductScopeEnum.STOCK]).pipe(
              filter((p) => !!p),
              map((productData) => {
                const formattedPrices = this.productMapper.formatCardPrice(productData, this.isNewPriceManagementActivated);
                return {
                  ...product,
                  price: formattedPrices?.price,
                  discountedPrice: formattedPrices?.discountedPrice,
                  recommendedPrice: formattedPrices?.recommendedPrice,
                  stock: {
                    ...product?.stock,
                    lowStock: this.stockService.checkStock(productData?.price?.value, productData?.stock?.stockLevel)
                  }
                };
              })
            );
          } else {
            return of(product);
          }
        });

      combineLatest(updateProductPrices$).pipe(
        take(1)
      ).subscribe((updatedProducts: IProductCard[]) => {
        const updatedProductsMap = new Map(updatedProducts.map((product) => [product.code, product]));
        this.productCards = this.productCards.map((product) => {
          if (updatedProductsMap.has(product.code)) {
            return { ...product, ...updatedProductsMap.get(product.code) };
          }
          return product;
        });
        this.isSSR = false;
      });
    }
  }
}
